import React, { useState } from 'react';
import { sendTestEmail } from '../services/EmailService';
import emailjsConfig from '../services/emailjsConfig';

const EmailTest = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  const addLog = (message, type = 'info') => {
    setLogs(prev => [...prev, { message, type, timestamp: new Date().toISOString() }]);
  };

  const checkConfiguration = () => {
    addLog('Checking EmailJS configuration...', 'info');
    
    const issues = [];
    if (!emailjsConfig.serviceId) {
      issues.push('EmailJS Service ID is missing');
    }
    
    if (!emailjsConfig.templateId) {
      issues.push('EmailJS Template ID is missing');
    }
    
    if (!emailjsConfig.publicKey) {
      issues.push('EmailJS Public Key is missing');
    }
    
    if (issues.length === 0) {
      addLog('EmailJS configuration looks good!', 'success');
      addLog(`Service ID: ${emailjsConfig.serviceId}`, 'success');
      addLog(`Template ID: ${emailjsConfig.templateId}`, 'success');
      addLog('Public Key: [HIDDEN]', 'success');
    } else {
      issues.forEach(issue => addLog(issue, 'error'));
    }
  };

  const handleSendTestEmail = async () => {
    if (!email.trim()) {
      addLog('Please enter a valid email address', 'error');
      return;
    }

    setLoading(true);
    setStatus(null);
    addLog(`Attempting to send test email to ${email}...`, 'info');

    try {
      const result = await sendTestEmail(email);
      
      if (result.success) {
        setStatus({ type: 'success', message: 'Test email sent successfully!' });
        addLog('Email sent successfully!', 'success');
      } else {
        setStatus({ type: 'error', message: result.message });
        addLog(`Failed to send email: ${result.message}`, 'error');
      }
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: `Error: ${error.message || 'Unknown error occurred'}`
      });
      addLog(`Error occurred: ${error.message}`, 'error');
      
      if (error.status) {
        addLog(`Status code: ${error.status}`, 'error');
      }
      
      if (error.text) {
        addLog(`Error details: ${error.text}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <h1 className="text-2xl font-bold mb-6">EmailJS Test Page</h1>
      
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Configuration Check</h2>
        <button 
          onClick={checkConfiguration}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
        >
          Check EmailJS Configuration
        </button>
      </div>
      
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Send Test Email</h2>
        
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Recipient Email:</label>
          <input 
            type="email" 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email address"
            className="w-full p-2 border rounded"
          />
        </div>
        
        <button 
          onClick={handleSendTestEmail}
          disabled={loading}
          className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded disabled:opacity-50"
        >
          {loading ? 'Sending...' : 'Send Test Email'}
        </button>
        
        {status && (
          <div className={`mt-4 p-3 rounded ${
            status.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {status.message}
          </div>
        )}
      </div>
      
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Logs</h2>
        
        <div className="bg-gray-100 p-3 rounded h-64 overflow-y-auto font-mono text-sm">
          {logs.length === 0 ? (
            <p className="text-gray-500">No logs yet. Run a test to see logs.</p>
          ) : (
            logs.map((log, index) => (
              <div 
                key={index} 
                className={`mb-1 ${
                  log.type === 'error' ? 'text-red-600' : 
                  log.type === 'success' ? 'text-green-600' : 'text-gray-800'
                }`}
              >
                <span className="text-gray-500">[{new Date(log.timestamp).toLocaleTimeString()}]</span> {log.message}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailTest; 